import type { LegalAction, PlayerAction, Pt, RoomView } from '@chipless/shared';
import { betPresets, sizedAmountProblem } from '@chipless/shared';
import { Button, Group, SimpleGrid, Stack, Text } from '@mantine/core';
import { useState } from 'react';
import { AmountInput, parseAmount, type AmountValue } from '../components/AmountInput.js';
import { pt, ptWithUnit } from '../format.js';
import { useStore } from '../store.js';
import classes from './ActionBar.module.css';

/**
 * アクションバー（要件6.10〜6.12）。
 *
 * サーバから受け取った `legalActions` を描画するだけの層に保つ。min-raise や BB
 * オプション、オールイン特例の判定はここには無い。金額の範囲も `LegalAction` が持つ
 * 値をそのまま使う。
 */

type Sized = Extract<LegalAction, { type: 'BET' | 'RAISE' }>;

/**
 * 手番が来ていないときの一言。
 *
 * 状態を体言止めで置く。「〜を待っている」のような説明文は、画面を一瞬見て状況を把握する
 * 使い方に合わない。自分の番のときは「あなたの番」を出すので、対になる言い方に揃える。
 */
function waitingMessage(hand: RoomView['hand']): string {
  if (hand === null) return 'ハンドの開始待ち';
  if (hand.street === 'COMPLETE') return 'ハンド終了';
  if (hand.needsAward) return '勝者を入力中';
  if (hand.currentTurn === null) return 'ハンドの進行待ち';
  return '他のプレイヤーの番';
}

export function ActionBar({ view }: { view: RoomView }): React.ReactElement | null {
  const send = useStore((s) => s.send);
  const pending = useStore((s) => s.pending);
  const hand = view.hand;
  const actions = view.you.legalActions;
  const isMyTurn = view.you.isMyTurn;

  const sized: Sized | undefined = actions.find(
    (a): a is Sized => a.type === 'BET' || a.type === 'RAISE',
  );

  /**
   * 金額入力は、選択肢の範囲が変わった時点で最小値へ戻す。前のハンドや前のストリートの
   * 値が残ると誤送信の元になる。
   *
   * effect で setState せず、レンダリング時に導出する。effect にすると、範囲が変わった
   * 直後の1フレームだけ古い金額が表示され、その瞬間に押されうる。
   */
  const rangeKey = `${hand?.handSeq ?? -1}:${sized?.minTo ?? 0}:${sized?.maxTo ?? 0}`;
  const [typed, setTyped] = useState<{ key: string; value: AmountValue } | null>(null);
  const amountText: AmountValue = typed?.key === rangeKey ? typed.value : (sized?.minTo ?? 0);
  const setAmount = (value: AmountValue): void => setTyped({ key: rangeKey, value });

  /**
   * 入力中の値は書き換えない。単位 5 で 140 を打つ途中の 14 は不正だが、ここで丸めると
   * 140 に辿り着けない。判定はサーバの検証と同じ関数で行い、不正な間は確定ボタンだけを
   * 押せなくする。
   */
  const amount: Pt | null = parseAmount(amountText);
  const amountIssue =
    sized === undefined
      ? null
      : amount === null
        ? '金額を入力してください'
        : sizedAmountProblem(sized, amount);

  if (view.you.seatNo === null) return null;

  // 自分のターンでないときアクションボタンは一切描画しない（要件5.3）。バー自体は残して
  // レイアウトが跳ねないようにする。
  //
  // `data-hand-seq` は「この画面がどの判定点を映しているか」を外から観測するために出す。
  // E2E がこれを持たないと、送信が反映される前の画面を見て次のアクションを押しうる。
  if (hand === null || !isMyTurn) {
    return (
      <div
        className={classes.bar}
        data-testid="action-bar"
        data-my-turn="false"
        data-hand-seq={hand?.handSeq ?? ''}
      >
        <Text ta="center" c="dimmed">
          {waitingMessage(hand)}
        </Text>
      </div>
    );
  }

  const submit = (action: PlayerAction): void => {
    send({ type: 'action', handSeq: hand.handSeq, action });
  };

  const fold = actions.find((a) => a.type === 'FOLD');
  const check = actions.find((a) => a.type === 'CHECK');
  const call = actions.find((a) => a.type === 'CALL');
  const allin = actions.find((a) => a.type === 'ALLIN');

  const presets =
    sized === undefined
      ? []
      : betPresets(sized, { potTotal: hand.potTotal, currentBet: hand.currentBet });

  /** ベット / レイズのボタンの表記。全額のときはオールインと呼ぶ（履歴の表記と揃える）。 */
  const sizedLabel = (spec: Sized): string => {
    if (amountIssue !== null || amount === null) return spec.type === 'BET' ? 'ベット' : 'レイズ';
    if (amount === spec.maxTo) return `オールイン ${pt(amount)}`;
    return `${spec.type === 'BET' ? 'ベット' : 'レイズ'} ${pt(amount)}`;
  };

  const disabled = pending;

  return (
    <div
      className={`${classes.bar} ${classes.myTurn}`}
      data-testid="action-bar"
      data-my-turn="true"
      data-hand-seq={hand.handSeq}
    >
      <Stack gap="sm">
        <Text ta="center" fw={700} c="yellow.4" data-testid="my-turn">
          あなたの番
        </Text>

        {sized !== undefined && (
          <Stack gap="xs">
            <Group gap="xs" grow>
              {presets.map((preset) => (
                <Button
                  key={preset.label}
                  size="sm"
                  variant={amount === preset.to ? 'filled' : 'light'}
                  onClick={() => setAmount(preset.to)}
                  disabled={disabled}
                >
                  {preset.label}
                </Button>
              ))}
            </Group>
            <AmountInput
              data-testid="action-amount"
              value={amountText}
              onChange={setAmount}
              // 金額の増減はビッグブラインド単位を基準にする（要件6.11）。
              step={view.config.bigBlind}
              allowNegative={false}
              disabled={disabled}
              problem={amountIssue}
              description={`${pt(sized.minTo)} 〜 ${pt(sized.maxTo)}（${pt(sized.step)} 単位）`}
            />
          </Stack>
        )}

        {(check !== undefined || call !== undefined || sized !== undefined) && (
          <SimpleGrid cols={sized === undefined ? 1 : 2} spacing="xs">
            {check !== undefined && (
              <Button
                data-testid="action-check"
                color="blue"
                disabled={disabled}
                onClick={() => submit({ type: 'CHECK' })}
              >
                チェック
              </Button>
            )}
            {call !== undefined && (
              <Button
                data-testid="action-call"
                color="blue"
                disabled={disabled}
                onClick={() => submit({ type: 'CALL' })}
              >
                コール {pt(call.amount)}
              </Button>
            )}
            {sized !== undefined && (
              <Button
                data-testid="action-bet"
                color="teal"
                disabled={disabled || amountIssue !== null}
                onClick={() => amount !== null && submit({ type: sized.type, to: amount })}
              >
                {sizedLabel(sized)}
              </Button>
            )}
          </SimpleGrid>
        )}

        {/*
          オールイン専用のボタンは、金額を指定して出せない場面だけに置く。ベット / レイズが
          選べるなら ALL プリセットで全額に届き、同じ結果になる（スタックが 0 になった席は
          イベントの種類によらずオールイン扱いになる）。常に並べると、金額入力とボタンで
          同じことを2通り提示することになる。
        */}
        {allin !== undefined && sized === undefined && (
          <Group justify="center">
            <Button
              data-testid="action-allin"
              className={classes.wide}
              color="orange"
              disabled={disabled}
              onClick={() => submit({ type: 'ALLIN', to: allin.to })}
            >
              オールイン {ptWithUnit(allin.to)}
            </Button>
          </Group>
        )}

        {/* Fold は他のボタンから離して置く（要件6.12）。 */}
        {fold !== undefined && (
          <Group justify="center" className={classes.foldRow}>
            <Button
              data-testid="action-fold"
              className={classes.fold}
              color="red"
              variant="outline"
              disabled={disabled}
              onClick={() => submit({ type: 'FOLD' })}
            >
              フォールド
            </Button>
          </Group>
        )}
      </Stack>
    </div>
  );
}
